import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { MessageSquare, Calendar, BookOpen, Settings, LogOut, LayoutDashboard, BarChart2, Bot, Sparkles } from 'lucide-react';
import api from '../utils/api';
import { ReseptioIcon, ReseptioLogoFull } from './ReseptioLogo';

const navItems = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/conversations', icon: MessageSquare, label: 'Konuşmalar' },
  { to: '/reservations', icon: BookOpen, label: 'Rezervasyonlar' },
  { to: '/calendar', icon: Calendar, label: 'Takvim' },
  { to: '/reports', icon: BarChart2, label: 'Analitik' },
  { to: '/bot-control', icon: Bot, label: 'AI Asistan' },
  { to: '/settings', icon: Settings, label: 'Ayarlar' },
];

function PlanBanner({ planStatus }) {
  if (!planStatus) return null;
  const { plan, label, messagesUsed, messageLimit, daysLeft, status } = planStatus;

  // Starter/Pro: banner gösterme
  if (plan === 'starter' || plan === 'pro' || plan === 'STARTER' || plan === 'PRO') return null;

  const pct = messageLimit > 0 ? (messagesUsed / messageLimit) * 100 : 0;
  const isExpired = status === 'expired';
  const isHighUsage = pct >= 80;

  if (isExpired) {
    return (
      <div style={{
        background: '#fee2e2', color: '#991b1b', padding: '10px 20px',
        fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid #fca5a5',
      }}>
        <span>Deneme süreniz doldu — planınızı yükseltin</span>
        <button style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
          Yükselt
        </button>
      </div>
    );
  }

  const bg = isHighUsage ? '#fee2e2' : '#fefce8';
  const color = isHighUsage ? '#991b1b' : '#78350f';
  const borderColor = isHighUsage ? '#fca5a5' : '#fde68a';

  return (
    <div style={{
      background: bg, color, padding: '10px 20px', fontSize: 13, fontWeight: 500,
      display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1px solid ${borderColor}`,
    }}>
      <span style={{ fontWeight: 700 }}>Deneme</span>
      {daysLeft !== null && <span>{daysLeft} gün kaldı</span>}
      <span>{messagesUsed}/{messageLimit} mesaj kullanıldı</span>
      {isHighUsage && <span style={{ fontWeight: 700, color: '#dc2626' }}>Limit dolmak uzere!</span>}
      <button style={{
        marginLeft: 'auto', background: isHighUsage ? '#dc2626' : '#f97316',
        color: 'white', border: 'none', borderRadius: 6, padding: '4px 12px',
        cursor: 'pointer', fontSize: 12, fontWeight: 700,
      }}>
        Plani Yukselт
      </button>
    </div>
  );
}

export default function DashboardLayout() {
  const { user, hotel, logout } = useAuth();
  const navigate = useNavigate();
  const [planStatus, setPlanStatus] = useState(null);

  const handleLogout = () => { logout(); navigate('/login'); };

  useEffect(() => {
    api.get('/hotel/plan-status')
      .then(d => setPlanStatus(d))
      .catch(() => {});
  }, []);

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Sidebar */}
      <aside style={{
        width: 248,
        background: 'linear-gradient(180deg, #1e1b4b 0%, #1e293b 100%)',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        boxShadow: '4px 0 24px rgba(0,0,0,0.15)',
        position: 'relative',
        zIndex: 10,
      }}>
        {/* Decorative top gradient */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 200, background: 'linear-gradient(180deg, rgba(99,102,241,0.2) 0%, transparent 100%)', pointerEvents: 'none' }} />

        {/* Logo / Hotel */}
        <div style={{ padding: '22px 18px 18px', borderBottom: '1px solid rgba(255,255,255,0.08)', position: 'relative' }}>
          {/* Reseptio marka logosu */}
          <div style={{ marginBottom: 16 }}>
            <ReseptioLogoFull height={26} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <ReseptioIcon size={36} style={{ borderRadius: 10, flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, letterSpacing: '-0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {hotel?.name || 'Otel Paneli'}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                <Sparkles size={10} color="#a5b4fc" />
                <span style={{ fontSize: 10, color: '#a5b4fc', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {hotel?.plan || 'PLAN'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(165,180,252,0.5)', letterSpacing: '0.1em', padding: '8px 10px 6px', textTransform: 'uppercase' }}>
            Menü
          </div>
          {navItems.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 10, marginBottom: 2,
                color: isActive ? 'white' : 'rgba(148,163,184,0.9)',
                background: isActive ? 'linear-gradient(135deg, rgba(99,102,241,0.7), rgba(59,130,246,0.5))' : 'transparent',
                textDecoration: 'none', fontSize: 13.5, fontWeight: isActive ? 700 : 500,
                transition: 'all 0.2s',
                boxShadow: isActive ? '0 2px 8px rgba(99,102,241,0.3)' : 'none',
                backdropFilter: isActive ? 'blur(8px)' : 'none',
              })}
            >
              {({ isActive }) => (
                <>
                  <div style={{
                    width: 30, height: 30, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                    transition: 'all 0.2s',
                  }}>
                    <Icon size={16} />
                  </div>
                  {label}
                  {isActive && <div style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: '#a5b4fc' }} />}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User footer */}
        <div style={{ padding: '12px 10px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 10, marginBottom: 4 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, color: 'white', flexShrink: 0,
            }}>
              {(user?.name || 'U')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
              <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase' }}>{user?.role}</div>
            </div>
          </div>
          <button onClick={handleLogout} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 10,
            background: 'rgba(239,68,68,0.1)', border: 'none', color: '#fca5a5', cursor: 'pointer',
            fontSize: 13, width: '100%', transition: 'all 0.2s', fontWeight: 500,
          }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239,68,68,0.1)'; }}
          >
            <LogOut size={14} /> Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, overflow: 'auto', background: '#f0f2f8', display: 'flex', flexDirection: 'column' }}>
        <PlanBanner planStatus={planStatus} />
        <div style={{ flex: 1, overflow: 'auto' }}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
